import { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useAuth } from '../context/AuthContext';
import { isBERole, isBE35hRole } from '../lib/roles';
import { getWeekNumber } from '../utils/dateUtils';
import { computeVisibleRows } from '../utils/visibleRowsFilter';
import { formatDiversLabel } from '../utils/diversDisplayUtils';
import { getISOYear } from '../utils/overtimeUtils';
import { useTimesheetData } from '../hooks/useTimesheetData';
import { useTimesheetActions } from '../hooks/useTimesheetActions';
import { useTimesheetGD } from '../hooks/useTimesheetGD';
import { useTimesheetValidation } from '../hooks/useTimesheetValidation';
import { useTimesheetPDF } from '../hooks/useTimesheetPDF';
import { useToast } from '../hooks/useToast';
import type { Ventilation } from './timesheet/types';
import { VentilationModal } from './timesheet/VentilationModal';
import { TimesheetHeader } from './timesheet/TimesheetHeader';
import { WeekNavigator } from './timesheet/WeekNavigator';
import { ValidationPanel } from './timesheet/ValidationPanel';
import { ProjectSelector } from './timesheet/ProjectSelector';
import { SkipMonthBanner } from './timesheet/SkipMonthBanner';
import { useConfirm } from '../hooks/useConfirm';
import { MONTHS_FR } from '../utils/dateUtils';
import { HoursGrid } from './timesheet/HoursGrid';
import { IndemnitySection } from './timesheet/IndemnitySection';
import { TimesheetFooter } from './timesheet/TimesheetFooter';
import { SignatureModal } from './timesheet/SignatureModal';
import { SubmissionModal } from '../components/ui';

export default function Timesheet() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const { toast } = useToast();

  const effectiveUserId = employeeId || user?.id;
  const isDelegateMode = !!employeeId;

  const data = useTimesheetData(employeeId, effectiveUserId, isDelegateMode);
  const {
    projects, diversSubProjects, currentWeekStart,
    rows, setRows, indemnities, setIndemnities,
    sheetStatus, rejectionReason,
    setEntryMonth, showMonthTransition, setShowMonthTransition,
    trainingDays, trainingProjectId,
    ventilations, setVentilations,
    dayStatuses, setDayStatuses,
    targetEmployeeName, targetEmployeeRole, customWeeklyHours, overrideWeeklyHours,
    loadingData, plannedGDDays,
    weekDays, holidays, weekMonths, isStraddleWeek,
    activeEntryMonth, isOutsideEntryMonth, isOutsideClosureMonth,
    isLocked, isLockedForDay,
    isDayBlocked, isTraining,
    changeWeek, fetchWeekData,
  } = data;

  // isBE / isBE35h sur le rôle du TARGET (effective user), pas de l'admin
  // connecté. Sans ça, un admin saisissant pour un BE en mode délégué ne
  // voyait pas la VentilationModal → ventilations non saisies (bug audit
  // 23/05/2026). En mode standard, target = user, donc même résultat.
  const effectiveRole = isDelegateMode ? targetEmployeeRole : user?.role;
  const isBE = isBERole(effectiveRole);
  const isBE35h = isBE35hRole(effectiveRole);

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showVentilationModal, setShowVentilationModal] = useState<{ projectId: string, dateStr: string, totalHours: number } | null>(null);
  // Projets réactivés sur le mois actif (semaine à cheval : projet existant
  // dans rows côté autre mois, ajouté explicitement pour le mois courant)
  const [addedProjectIds, setAddedProjectIds] = useState<Set<string>>(new Set());
  // Reset quand on change de mois actif ou de semaine
  useEffect(() => { setAddedProjectIds(new Set()); }, [activeEntryMonth, currentWeekStart]);

  const sigCanvas = useRef<SignatureCanvas>(null);

  // MONTHS_FR moved to sub-components

  // --- GD & ZONES (extracted into useTimesheetGD) ---
  const { getGDWorkDaysInfo, getGDProjectForDay, calculateDailyWinningZone } = useTimesheetGD({
    rows, indemnities, weekDays, holidays, trainingDays,
    projects, diversSubProjects, isBE35h, isOutsideEntryMonth,
  });

  // --- ACTIONS (extracted into useTimesheetActions) ---
  const {
    handleHoursChange,
    handleRemoveProject,
    handleIndemnityChange,
    calculateTotal,
    saveToDatabase,
    handleSaveDraft,
    submitEmptyMonth,
  } = useTimesheetActions({
    rows, setRows, indemnities, setIndemnities,
    ventilations, setVentilations,
    weekDays, trainingDays, trainingProjectId,
    projects, diversSubProjects, currentWeekStart,
    effectiveUserId, isBE, isBE35h, isLocked, isLockedForDay,
    isOutsideEntryMonth, getGDProjectForDay,
    activeEntryMonth, dayStatuses, setDayStatuses,
    customWeeklyHours, overrideWeeklyHours, setSaving: setSaving,
  });

  const { validationResult } = useTimesheetValidation({
    rows, indemnities, ventilations,
    weekDays, holidays, trainingDays,
    projects, isBE, plannedGDDays,
    isOutsideEntryMonth, isLockedForDay,
  });

  // Semaine à cheval : logique extraite dans src/utils/visibleRowsFilter.ts
  // (testable unitairement). Règles : garder les rows avec heures sur le
  // mois actif, les rows vides (fraîchement ajoutées), celles explicitement
  // ré-ajoutées, et (BE uniquement) celles avec ventilation incomplète.
  const visibleRows = useMemo(() => computeVisibleRows({
    rows, weekDays, isStraddleWeek, isOutsideEntryMonth,
    addedProjectIds, isBE, ventilations,
  }), [rows, weekDays, isStraddleWeek, isOutsideEntryMonth, addedProjectIds, isBE, ventilations]);

  const handleAddProject = () => {
    if (isLocked) return;
    if (!selectedProjectId) return;

    // Gestion des sous-chantiers Divers (format "sub:UUID")
    if (selectedProjectId.startsWith('sub:')) {
      const subId = selectedProjectId.replace('sub:', '');
      const subProject = diversSubProjects.find(s => s.id === subId);
      if (!subProject) return;

      // Identifiant interne unique pour ce sous-chantier dans la grille
      const internalId = `divers:${subId}`;

      // Vérifier si déjà affiché dans le mois actif
      if (visibleRows.find(r => r.projectId === internalId)) {
        toast.warning("Ce chantier Divers est déjà affiché.");
        return;
      }

      if (rows.find(r => r.projectId === internalId)) {
        // Le projet existe dans rows (heures sur l'autre mois) : le rendre visible
        setAddedProjectIds(prev => new Set(prev).add(internalId));
      } else {
        const parentProject = projects.find(p => p.id === subProject.parent_project_id);
        setRows([...rows, {
          projectId: internalId,
          // Format `26E0259 - FIMOR` : code du projet parent (= numéro
          // d'affaire utile à la facturation) suivi du nom client.
          // Demande J.-L. OGER (mai 2026) — voir diversDisplayUtils.ts.
          projectName: formatDiversLabel(parentProject?.code ?? '???', subProject.name),
          hours: {},
          descriptions: {},
          zones: {}
        }]);
      }
      setSelectedProjectId('');
      return;
    }

    // Gestion des projets normaux
    const project = projects.find(p => p.id === selectedProjectId);
    if (!project) return;
    if (project.code === 'SYS-FORM') return; // Ne pas ajouter manuellement le projet formation
    if (visibleRows.find(r => r.projectId === selectedProjectId)) { toast.warning("Ce chantier est déjà affiché."); return; }

    if (rows.find(r => r.projectId === selectedProjectId)) {
      // Le projet existe dans rows (heures sur l'autre mois) : le rendre visible
      setAddedProjectIds(prev => new Set(prev).add(selectedProjectId));
    } else {
      setRows([...rows, {
          projectId: project.id,
          projectName: `${project.code} - ${project.name}`,
          hours: {},
          descriptions: {},
          zones: {}
      }]);
    }
    setSelectedProjectId('');
  };

  const handleOpenVentilation = (projectId: string, dateStr: string, totalHours: number) => {
    // NOUVEAU : Vérifier le verrouillage jour par jour (pour semaines à cheval)
    const dayDate = new Date(dateStr);
    if (isLockedForDay(dayDate)) return;
    setShowVentilationModal({ projectId, dateStr, totalHours });
  };

  const saveVentilation = (v: Ventilation) => {
    if (!showVentilationModal) return;
    const key = `${showVentilationModal.projectId}_${showVentilationModal.dateStr}`;
    setVentilations({ ...ventilations, [key]: v });
    setShowVentilationModal(null);
  };

  // --- PDF & SIGNATURE (extracted to useTimesheetPDF) ---
  const { handleFinalSign } = useTimesheetPDF({
    rows, weekDays, holidays, trainingDays, indemnities,
    currentWeekStart, isDelegateMode, targetEmployeeName,
    userName: user?.full_name,
    isBE35h, customWeeklyHours, overrideWeeklyHours,
    calculateTotal, calculateDailyWinningZone, getGDProjectForDay,
    saveToDatabase, fetchWeekData,
    setSaving, setShowSignatureModal,
    onSuccess: () => setShowSuccessModal(true),
    navigate, sigCanvas, toast,
  });

  const clearSignature = () => sigCanvas.current?.clear();

  // Handler "Je n'aurai pas d'heures à saisir en {mois}" : confirm dialog
  // pour éviter le clic accidentel (l'action verrouille le mois et fait
  // basculer la feuille en "À valider"), puis appel du hook.
  const { confirm: confirmDialog } = useConfirm();
  const handleSkipMonth = async () => {
    if (!activeEntryMonth) return;
    const monthLabel = `${MONTHS_FR[activeEntryMonth.month]} ${activeEntryMonth.year}`;
    const ok = await confirmDialog({
      title: `Confirmer : aucune heure en ${monthLabel} ?`,
      message:
        `Cette action soumettra les jours de ${monthLabel} de cette semaine avec 0h. ` +
        `La feuille sortira de "En cours" et sera envoyée pour validation. ` +
        `Vous ne pourrez plus modifier ce mois sans l'aide d'un admin.`,
      variant: 'info',
      confirmLabel: 'Confirmer',
    });
    if (!ok) return;
    try {
      await submitEmptyMonth();
      toast.success(`${monthLabel} soumis sans heures. Feuille envoyée pour validation.`);
      await fetchWeekData();
    } catch (e) {
      toast.error("Erreur lors de la soumission : " + (e instanceof Error ? e.message : String(e)));
    }
  };

  return (
    <div style={{ paddingBottom: '120px', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
      
      <TimesheetHeader
        sheetStatus={sheetStatus}
        employeeName={isDelegateMode ? targetEmployeeName : user?.full_name}
        isDelegateMode={isDelegateMode}
        isLocked={isLocked}
        rejectionReason={rejectionReason}
        onBack={() => navigate(isDelegateMode ? '/team' : '/')}
      />

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        
        <WeekNavigator
          currentWeekStart={currentWeekStart}
          weekMonths={weekMonths}
          isStraddleWeek={isStraddleWeek}
          activeEntryMonth={activeEntryMonth}
          showMonthTransition={showMonthTransition}
          changeWeek={changeWeek}
          setEntryMonth={setEntryMonth}
          setShowMonthTransition={setShowMonthTransition}
          holidays={holidays}
          trainingDays={trainingDays}
          isBE={isBE}
          isBE35h={isBE35h}
          customWeeklyHours={customWeeklyHours}
          overrideWeeklyHours={overrideWeeklyHours}
          rows={rows}
          indemnities={indemnities}
          isDayBlocked={isDayBlocked}
        />

        <SkipMonthBanner
          isStraddleWeek={isStraddleWeek}
          activeEntryMonth={activeEntryMonth}
          weekDays={weekDays}
          rows={rows}
          dayStatuses={dayStatuses}
          isLocked={isLocked}
          isSaving={saving}
          onSkipMonth={handleSkipMonth}
        />

        <ValidationPanel
          validationResult={validationResult}
          loadingData={loadingData}
          hasHoursEntered={rows.some(r => Object.values(r.hours).some(h => h > 0))}
        />

        {loadingData ? <div style={{textAlign:'center', padding: '80px', color:'#94a3b8', fontSize:'1.2rem'}}>Chargement des données...</div> : (
          <>
            {isBE && (
              <div style={{ background: '#eff6ff', color: '#1e40af', padding: '20px', borderRadius: '16px', marginBottom: '24px', border: '1px solid #dbeafe', fontSize:'1rem' }}>
              <strong>Bureau d'Étude :</strong> Veuillez ventiler vos heures en cliquant sur les cases remplies.
              </div>
            )}

            <ProjectSelector
              projects={projects}
              diversSubProjects={diversSubProjects}
              selectedProjectId={selectedProjectId}
              isLocked={isLocked}
              onSelectChange={setSelectedProjectId}
              onAdd={handleAddProject}
            />

            {/* GRILLE PRINCIPALE */}
            <HoursGrid
              rows={visibleRows}
              weekDays={weekDays}
              isLocked={isLocked}
              isBE={isBE}
              ventilations={ventilations}
              isOutsideClosureMonth={isOutsideClosureMonth}
              isLockedForDay={isLockedForDay}
              isDayBlocked={isDayBlocked}
              isTraining={isTraining}
              onHoursChange={(visIdx, dateStr, value) => {
                const realIdx = rows.findIndex(r => r.projectId === visibleRows[visIdx].projectId);
                if (realIdx >= 0) handleHoursChange(realIdx, dateStr, value);
              }}
              onRemoveProject={(visIdx) => {
                const realIdx = rows.findIndex(r => r.projectId === visibleRows[visIdx].projectId);
                if (realIdx >= 0) handleRemoveProject(realIdx);
              }}
              onOpenVentilation={handleOpenVentilation}
            />

            {/* SECTION INDEMNITÉS */}
            <IndemnitySection
              indemnities={indemnities}
              weekDays={weekDays}
              holidays={holidays}
              isOutsideClosureMonth={isOutsideClosureMonth}
              isLockedForDay={isLockedForDay}
              isDayBlocked={isDayBlocked}
              isTraining={isTraining}
              onIndemnityChange={handleIndemnityChange}
              getGDWorkDaysInfo={getGDWorkDaysInfo}
              rows={rows}
            />
          </>
        )}
      </div>

      <TimesheetFooter
        totalHours={calculateTotal()}
        sheetStatus={sheetStatus}
        saving={saving}
        loadingData={loadingData}
        isLocked={isLocked}
        validationResult={validationResult}
        isDelegateMode={isDelegateMode}
        onSaveDraft={() => handleSaveDraft(() => setShowDraftModal(true))}
        onSignAndSubmit={() => {
          if (validationResult.errors.length > 0) {
            toast.warning(`Impossible de soumettre : ${validationResult.errors.length} erreur(s) à corriger. ${validationResult.errors.map(e => e.message).join(' | ')}`);
            return;
          }
          setShowSignatureModal(true);
        }}
      />

      <SignatureModal
        isOpen={showSignatureModal}
        isDelegateMode={isDelegateMode}
        targetEmployeeName={targetEmployeeName}
        saving={saving}
        sigCanvasRef={sigCanvas}
        onClear={clearSignature}
        onCancel={() => setShowSignatureModal(false)}
        onValidate={handleFinalSign}
      />

      {/* MODALE VENTILATION (BE) */}
      {showVentilationModal && (
        <VentilationModal
          totalHours={showVentilationModal.totalHours}
          onSave={saveVentilation}
          onCancel={() => setShowVentilationModal(null)}
          existing={ventilations[`${showVentilationModal.projectId}_${showVentilationModal.dateStr}`]}
        />
      )}

      <SubmissionModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          if (isDelegateMode) navigate('/team');
        }}
        title="Feuille transmise !"
        message={`La feuille d'heures de ${isDelegateMode ? targetEmployeeName : (user?.full_name || '')} pour la semaine ${getWeekNumber(currentWeekStart)}/${getISOYear(currentWeekStart)} a été signée et transmise avec succès.`}
        detail="Elle est maintenant en attente de validation par la direction."
        variant="success"
      />

      <SubmissionModal
        isOpen={showDraftModal}
        onClose={() => setShowDraftModal(false)}
        title="Brouillon enregistré"
        message="Vos heures ont été sauvegardées. Vous pouvez les modifier et les soumettre plus tard."
        variant="draft"
      />

    </div>
  );
}

// VentilationModal extracted to ./timesheet/VentilationModal.tsx